import { DriveListItemStatus } from '@/user_files/drive/constants'
import { UserFileProcessingStatus } from '@/user_files/schemas/processing-status'

/** Date and time segments for the modified column (6px gap between in UI). */
export function formatDriveListUpdatedAtParts(iso: string | null | undefined): { date: string; time: string } {
  if (!iso) return { date: '', time: '' }
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return { date: '', time: '' }
  return {
    // date format: MM/DD/YYYY
    date: d.toLocaleDateString(undefined, {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    }),
    // time format: HH:MM AM/PM
    time: d.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    }),
  }
}

/** Single-line fallback (e.g. aria-label); prefer `formatDriveListUpdatedAtParts` in layouts. */
export function formatDriveListUpdatedAt(iso: string | null | undefined): string {
  const { date, time } = formatDriveListUpdatedAtParts(iso)
  return [date, time].filter(Boolean).join(' ')
}

/** Format bytes to human-readable size string (e.g. "24 KB", "1.2 MB"). */
export function formatFileSize(bytes: number | null | undefined, fractionDigits: number = 1): string {
  if (bytes == null || bytes === 0) return '0 B'
  const k = 1000
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1)
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(fractionDigits))} ${sizes[i]}`
}

/** Folder Size cell: the recursive count of visible files in the folder (e.g. "1 file", "22 files"). */
export function formatDriveListFileCount(fileCount: number): string {
  return fileCount === 1 ? '1 file' : `${fileCount} files`
}

/**
 * Maps API `processing_status` to drive list row status.
 */
export function fileProcessingStatusToDriveStatus(status: string | null | undefined): DriveListItemStatus {
  if (!status) return DriveListItemStatus.Pending
  switch (status) {
    case UserFileProcessingStatus.Pending:
      return DriveListItemStatus.Pending
    case UserFileProcessingStatus.Processing:
      return DriveListItemStatus.Processing
    case UserFileProcessingStatus.Completed:
      return DriveListItemStatus.Completed
    case UserFileProcessingStatus.Failed:
      return DriveListItemStatus.Failed
    default:
      return DriveListItemStatus.Pending
  }
}
